// @flow
import React, { Component } from 'react';
import _ from 'lodash';
import { Segment, Grid, Form, Header, Icon, Button, Message } from 'semantic-grab-ui/src';
import formComponent from 'components/decorators/form_component';
import FormInputWithError from 'components/form/FormInputWithError';
import type { TridentCampaignDetails } from 'types/trident-campaigns';
import Toastr from 'utils/toastr-util';
import SubCountCondition from './SubCountCondition';
import { getCountConditionVarName } from './util';
import './TreatmentForm.scss';

type Props = {
  model: TridentCampaignDetails,
  canActivate: boolean,
  currentNode: string,
  parentNode: string,
  parentKeyList: Array<string>,
  onModelChange: (_?: Object) => void,
  handleInputChange: Function,
  handleControlBlur: Function,
  handleCloseModal: Function,
  isAdd: boolean,
  handleDeleteNode: Function,
  isValid: Function,
  error: any,
};

const IsMultipleOfOperator = 'im';

function hasDecimalPoint(number) {
  return number % 1 !== 0;
}

@formComponent()
export default class CountConditionForm extends Component {
  props: Props;

  constructor(props: Props) {
    super(props);
    this.state = {
      conditions: this.convertFromModelCondition(_.get(props.model.nodes[this.props.currentNode], 'data', {})),
    };
  }

  convertFromModelCondition = (cond: Object) => {
    const conds = [];
    if (!_.isEmpty(cond.lhs)) {
      conds.push({
        lhs: cond.lhs.split('+'),
        operator: cond.operator,
        rhs: cond.rhs,
      });
    }

    _.map(_.get(cond, 'conditions', []), (c) => {
      conds.push({
        lhs: c.lhs.split('+'),
        operator: c.operator,
        rhs: c.rhs,
      });
    });

    return conds;
  };

  convertToModelCondition = (conditions: Array<Object>) => {
    if (conditions.length === 0) {
      return {};
    }
    if (conditions.length === 1) {
      return {
        lhs: _.get(conditions[0], 'lhs', []).join('+'),
        operator: conditions[0].operator,
        rhs: Number(conditions[0].rhs),
      };
    }
    const subConds = _.map(conditions, (cond) => {
      return {
        lhs: _.get(cond, 'lhs', []).join('+'),
        operator: cond.operator,
        rhs: Number(cond.rhs),
      };
    });
    return {
      operator: 'and',
      conditions: subConds,
    };
  };

  getCountConditionOptions = (targets: Array<string>, scenarios: Array<Object>) => {
    return _.map(targets, (target, idx) => {
      const index = idx + 1; // one-based index
      return {
        key: index.toString(),
        value: index.toString(),
        text: getCountConditionVarName(target, scenarios),
      };
    });
  };

  scrollToBottom = () => {
    this.countConditionEnd.scrollIntoView({ behavior: 'smooth' });
  };

  componentDidMount() {
    this.scrollToBottom();
  }

  handleAddCountCondition = () => {
    const conditions = this.state.conditions;
    conditions.push({});
    this.setState({
      conditions,
    });
  };

  handleDeleteCountCondition = (index: number) => {
    const conditions = _.cloneDeep(this.state.conditions);
    conditions.splice(index, 1);
    this.setState({
      conditions,
    });
  };

  handleCountConditionChange = (data: any, index: number) => {
    const conditions = this.state.conditions;
    conditions[index] = {
      ...conditions[index],
      [data.name]: data.value,
    };
    this.setState({
      conditions,
    });
  };

  cancelCountCondition = () => {
    if (this.props.isAdd) {
      // eslint-disable-next-line no-alert
      const confirmValue = confirm('Cancel will delete this inactive node. Are you sure to delete this?');
      if (!confirmValue) return;
      this.props.handleDeleteNode(this.props.parentNode, this.props.currentNode, -1);
    }
    this.props.handleCloseModal();
  };

  saveCountCondition = () => {
    // validation
    if (_.isEmpty(this.state.conditions)) {
      Toastr.error('Count condition cannot be empty');
      return;
    }

    const parentNode = this.props.model.nodes[this.props.parentNode];
    const isValid = this.validateInput(this.state.conditions, parentNode);
    if (!isValid) {
      return;
    }

    const updatedModel = _.cloneDeep(this.props.model);
    _.set(updatedModel.nodes[this.props.currentNode], 'data', this.convertToModelCondition(this.state.conditions));
    this.props.onModelChange(updatedModel);
    this.props.handleCloseModal();
  };

  validateInput = (conditions, parentNode) => {
    const parentCounterTypes = _.get(parentNode, 'data.targets', []);
    for (let i = 0; i < conditions.length; i += 1) {
      const cond = conditions[i];
      if (_.isEmpty(cond.lhs) || _.isEmpty(cond.operator)) {
        Toastr.error('Incomplete condition is not allowed');
        return false;
      }

      const isLhsOnlyContainsOccurrences = this.isLhsOnlyContainsOccurrences(cond.lhs, parentCounterTypes);
      if (cond.operator === IsMultipleOfOperator) {
        if (!isLhsOnlyContainsOccurrences) {
          Toastr.error('\'is a multiple of\' operator only applicable for occurrence');
          return false;
        }
      }

      if (isLhsOnlyContainsOccurrences && hasDecimalPoint(cond.rhs)) {
        Toastr.error(`Occurrence count can only be compared with whole number. Invalid number: ${cond.rhs}`);
        return false;
      }
    }
    return true;
  }

  isLhsOnlyContainsOccurrences = (lhs, counterTypes) => {
    for (let lhsIndex = 0; lhsIndex < lhs.length; lhsIndex += 1) {
      const countTypeIndex = lhs[lhsIndex] - 1;
      if (!counterTypes[countTypeIndex].startsWith('occurrence')) {
        return false;
      }
    }
    return true;
  }

  render() {
    const { model, currentNode } = this.props;

    const countTargets = _.get(this.props.model.nodes[this.props.parentNode], 'data.targets', []);
    const scenarios = _.get(this.props.model.nodes[this.props.parentKeyList[0]], 'data.scenarios', []);
    const countConditionOptions = this.getCountConditionOptions(countTargets, scenarios);
    const conditions = this.state.conditions;
    const isConditionsEmpty = _.isEmpty(conditions);
    return (
      <div>
        <div className="model-button">
          <Grid>
            <Grid.Column textAlign="right">
              <Button.Group>
                <Button
                  onClick={this.cancelCountCondition}
                >
                  Cancel
                </Button>
                <Button.Or />
                <Button
                  positive
                  onClick={this.saveCountCondition}
                >
                  Save
                </Button>
              </Button.Group>
            </Grid.Column>
          </Grid>
        </div>
        <div className="treatment-form">
          <Segment className="treatment-form-segment">
            <Grid>
              <Grid.Column width={12}>
                <Header as="h3">Count Condition Name</Header>
              </Grid.Column>
            </Grid>
            <Grid>
              <Grid.Column width={12}>
                <Form>
                  <Grid>
                    <Grid.Column width={12}>
                      <FormInputWithError
                        name={'nodes.'.concat(`${currentNode}`, '.name')}
                        {...this.props}
                      >
                        <Form.Input
                          required
                          value={model.nodes[currentNode].name}
                          onChange={this.props.handleInputChange}
                          onBlur={this.props.handleControlBlur}
                        />
                      </FormInputWithError>
                    </Grid.Column>
                  </Grid>
                </Form>
              </Grid.Column>
            </Grid>
          </Segment>
          <Segment className="treatment-form-segment">
            <Grid columns="equal">
              <Grid.Column>
                <Header as="h3" style={{ paddingTop: '15px' }}>Builder</Header>
              </Grid.Column>
              <Grid.Column textAlign="right">
                <Button onClick={this.handleAddCountCondition}>Add Condition</Button>
              </Grid.Column>
            </Grid>
            {!_.isEmpty(_.get(this.props.error, 'countCondition', [])) &&
              <Message negative size="tiny">
                <Message.Header>
                  <span className="validation-icon" />
                  {this.props.error.countCondition[0]}
                </Message.Header>
              </Message>
            }
            {isConditionsEmpty &&
              <Segment placeholder size="tiny" textAlign="center">
                <Header icon>
                  <Icon name="tag" />
                  No count-conditions are set yet!
                </Header>
              </Segment>
            }
            {conditions.map((condition, index) => {
              return (
                <Segment key={index}>
                  <SubCountCondition
                    onDelete={() => this.handleDeleteCountCondition(index)}
                    options={countConditionOptions}
                    onChange={(data) => this.handleCountConditionChange(data, index)}
                    lhs={condition.lhs}
                    operator={condition.operator}
                    rhs={condition.rhs}
                  />
                </Segment>
              );
            })}
          </Segment>
        </div>
        <div
          style={{ float: 'left', clear: 'both' }}
          ref={(el) => { this.countConditionEnd = el; }}
        />
      </div>
    );
  }
}
